import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import useLenis from "@/lib/useLenis";
import useSEO, { useJsonLd } from "@/lib/useSEO";
import { useContentWithStatus } from "@/lib/useContent";
import NotFound from "@/pages/NotFound";
import Nav from "@/sections/Nav";
import Footer from "@/sections/Footer";
import { Toaster } from "@/components/ui/sonner";
import { BookingForm } from "@/components/BookingForm";
import { JOURNEYS } from "@/lib/packages-data";
import { CONTACT, SITE_URL } from "@/lib/site-data";

const EASE = [0.76, 0, 0.24, 1];

export default function PackageDetail() {
  const { slug } = useParams();
  useLenis();
  const { data: journeys, loaded } = useContentWithStatus("journeys", JOURNEYS);
  const j = journeys.find((x) => x.slug === slug);
  useSEO(
    j
      ? {
          title: `${j.title} · ${j.duration} Bhutan ${j.category} Package`,
          description: j.intro,
          path: `/journeys/${j.slug}`,
          image: j.img,
        }
      : {}
  );
  useJsonLd(
    "trip",
    j
      ? [
          {
            "@context": "https://schema.org",
            "@type": "TouristTrip",
            name: j.title,
            description: j.intro,
            touristType: j.category,
            image: j.img,
            url: `${SITE_URL}/journeys/${j.slug}`,
            itinerary: {
              "@type": "ItemList",
              numberOfItems: j.itinerary.length,
              itemListElement: j.itinerary.map((d) => ({
                "@type": "ListItem",
                position: d.day,
                name: `Day ${d.day}: ${d.title}`,
                description: d.detail,
              })),
            },
            offers: {
              "@type": "Offer",
              price: parseFloat((j.from || "").replace(/[^0-9.]/g, "")) || undefined,
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
            },
            provider: {
              "@type": "TravelAgency",
              name: "Planet Air Adventure",
              url: SITE_URL,
              telephone: "+975 17904838",
              email: CONTACT.email,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Journeys", item: `${SITE_URL}/journeys` },
              { "@type": "ListItem", position: 3, name: j.title, item: `${SITE_URL}/journeys/${j.slug}` },
            ],
          },
        ]
      : null
  );
  // Journeys added in the admin panel aren't in the built-in list, so wait for the
  // live list before deciding a page doesn't exist (previously these redirected away).
  if (!j && !loaded) return <div className="min-h-screen bg-background" aria-busy="true" />;
  if (!j) return <NotFound />;

  const idx = journeys.findIndex((x) => x.slug === slug);
  const next = journeys[(idx + 1) % journeys.length];

  return (
    <div className="App relative" key={slug}>
      <div className="grain" aria-hidden />
      <Nav />
      <main data-testid="package-detail-page">
        <DetailHero j={j} />
        <MetaBar j={j} />
        <IntroSection j={j} />
        <ItinerarySection j={j} />
        <InclusionsSection j={j} />
        <GalleryStrip j={j} />
        <BookingSection j={j} />
        <NextJourney next={next} />
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function DetailHero({ j }) {
  return (
    <section className="relative h-[72vh] min-h-[520px] overflow-hidden bg-[#0A0B0A]">
      <motion.img
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
        src={j.img}
        alt={j.title}
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0A] via-transparent to-[#0A0B0A]/40" />
      <div className="relative h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-14 md:pb-20 text-[#F5F2EB]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#F5F2EB]/70 mb-5 flex items-center gap-3 flex-wrap">
            <Link to="/journeys" data-testid="breadcrumb-journeys" className="hover:text-[#D1492E] transition-colors">
              Journeys
            </Link>
            <span aria-hidden>/</span>
            <span className="text-[#D1492E]">{j.category}</span>
            <span aria-hidden>·</span>
            <span>No. {j.number}</span>
          </div>
          <h1 data-testid="package-title" className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.94] tracking-tighter max-w-4xl">
            {j.title}
          </h1>
          <div className="mt-4 text-base md:text-lg font-light text-[#F5F2EB]/75">
            {j.subtitle}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function MetaBar({ j }) {
  return (
    <section className="border-b border-foreground/15 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-6 py-8 md:py-10">
        <Meta label="Duration" value={j.duration} testid="meta-duration" />
        <Meta label="From" value={j.from} sub={j.perPerson} testid="meta-price" />
        <Meta label="Grade" value={j.difficulty} testid="meta-grade" />
        <Meta label="Season" value={j.season} testid="meta-season" />
      </div>
    </section>
  );
}

function IntroSection({ j }) {
  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <Kicker>The Journey</Kicker>
          <Reveal>
            <p className="font-serif font-light text-2xl md:text-4xl leading-[1.25] tracking-tight">
              {j.intro}
            </p>
          </Reveal>
          <div className="mt-8 text-sm text-foreground/60">{j.groupSize}</div>
        </div>
        <div className="md:col-span-5 md:pt-12">
          <div className="border border-foreground/15 p-7 md:p-9">
            <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-6">
              Highlights
            </div>
            <ul className="space-y-4">
              {j.highlights.map((h) => (
                <li key={h} className="flex gap-4 text-base font-light text-foreground/80 leading-relaxed">
                  <span className="mt-2.5 w-1.5 h-1.5 bg-primary rotate-45 shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ItinerarySection({ j }) {
  return (
    <section className="py-20 md:py-28 bg-[#0A0B0A] text-[#F5F2EB]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#F5F2EB]/60 mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[#D1492E]" />
          <span>The route, day by day</span>
        </div>
        <Reveal>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.96] tracking-tighter mb-14 md:mb-20">
            {j.itinerary.length} days, <em className="italic text-[#D1492E]">unhurried.</em>
          </h2>
        </Reveal>
        <div data-testid="itinerary-list">
          {j.itinerary.map((d) => (
            <Reveal key={d.day}>
              <div className="grid md:grid-cols-12 gap-3 md:gap-10 py-7 md:py-8 border-t border-[#F5F2EB]/15 group">
                <div className="md:col-span-2 font-serif text-2xl md:text-3xl text-[#F5F2EB]/50 group-hover:text-[#D1492E] transition-colors">
                  Day {String(d.day).padStart(2, "0")}
                </div>
                <div className="md:col-span-4 font-serif text-2xl md:text-3xl font-light tracking-tight">
                  {d.title}
                </div>
                <div className="md:col-span-6 text-base font-light text-[#F5F2EB]/65 leading-relaxed max-w-xl">
                  {d.detail}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InclusionsSection({ j }) {
  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16">
        <div data-testid="includes-list">
          <Kicker>What's included</Kicker>
          <ul className="space-y-4 border-t border-foreground/15 pt-6">
            {j.includes.map((x) => (
              <li key={x} className="flex gap-4 text-base font-light text-foreground/80 leading-relaxed">
                <span className="text-primary shrink-0">✓</span>
                {x}
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="excludes-list">
          <Kicker>Not included</Kicker>
          <ul className="space-y-4 border-t border-foreground/15 pt-6">
            {j.excludes.map((x) => (
              <li key={x} className="flex gap-4 text-base font-light text-foreground/60 leading-relaxed">
                <span className="shrink-0">—</span>
                {x}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-foreground/55 leading-relaxed">
            Flying in? We book every Drukair & Bhutan Airlines route into
            Paro alongside your journey — call {CONTACT.phone1} for fares.
          </p>
        </div>
      </div>
    </section>
  );
}

function GalleryStrip({ j }) {
  return (
    <section className="pb-20 md:pb-28 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-3 gap-3 md:gap-6">
        {j.gallery.map((g, i) => (
          <Reveal key={g}>
            <div className={`relative overflow-hidden bg-muted ${i === 1 ? "aspect-[3/4] -mt-6 md:-mt-10" : "aspect-[3/4]"}`}>
              <img src={g} alt={`${j.title} — ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover duotone" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BookingSection({ j }) {
  return (
    <section id="book" className="py-20 md:py-28 bg-background border-t border-foreground/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Kicker>Reserve</Kicker>
          <Reveal>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.96] tracking-tighter">
              Make it <em className="italic text-primary">yours.</em>
            </h2>
          </Reveal>
          <p className="mt-6 text-base md:text-lg text-foreground/70 font-light leading-relaxed max-w-md">
            Send a booking inquiry and a named curator in Paro will confirm
            availability, tailor the route to your pace, and hold your
            dates — all within 24 hours.
          </p>
          <div className="mt-10 space-y-2 text-sm text-foreground/60">
            <div>{CONTACT.phone1} · {CONTACT.phone2}</div>
            <div>{CONTACT.email}</div>
          </div>
        </div>
        <div className="md:col-span-7">
          <BookingForm interest={j.title} />
        </div>
      </div>
    </section>
  );
}

function NextJourney({ next }) {
  return (
    <section className="border-t border-foreground/10 bg-background">
      <Link
        to={`/journeys/${next.slug}`}
        data-testid="next-journey-link"
        className="group block mx-auto max-w-[1400px] px-6 md:px-10 py-14 md:py-20"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/55 mb-4">
          Next journey
        </div>
        <div className="flex items-end justify-between gap-6">
          <h3 className="font-serif font-light text-4xl md:text-6xl tracking-tighter group-hover:text-primary transition-colors">
            {next.title}
          </h3>
          <span aria-hidden className="text-3xl md:text-5xl transition-transform duration-500 group-hover:translate-x-3">
            →
          </span>
        </div>
      </Link>
    </section>
  );
}

function Meta({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="pr-6">
      <div className="text-[10px] uppercase tracking-[0.28em] text-foreground/55 mb-1.5">
        {label}
      </div>
      <div className="font-serif text-xl md:text-2xl font-light leading-tight">{value}</div>
      {sub && <div className="text-xs text-foreground/55 mt-1">{sub}</div>}
    </div>
  );
}

function Kicker({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-6 flex items-center gap-3">
      <span className="w-6 h-px bg-primary" />
      <span>{children}</span>
    </div>
  );
}

function Reveal({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.85, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}
